// One side of the A/B comparison, animated like a real chat reply: a pre-reveal
// blank delay (set by the dialog so both sides sync), then the typing indicator
// for its hold, then the reveal (streamed paragraph by paragraph, typed
// character by character, or instant). Calls onReady when the reveal finishes
// so the dialog can unlock selection once BOTH sides are done. Reuses the shared
// typewriter engine, the typing indicator, and the main chat's assistant bubble.

import { useEffect, useRef, useState } from "react";
import AssistantMarkdown from "./AssistantMarkdown";
import TypingIndicator from "./TypingIndicator";
import { runTypewriter } from "./typewriter";
import type { AbPaneRaw } from "./presentation";

type Phase = "blank" | "typing" | "reveal";
const STREAM_CHUNK_MS = 700; // desktop _show_next_chunk interval

export default function AbTestPane({
  text,
  raw,
  blankMs,
  onReady,
}: {
  text: string;
  raw: AbPaneRaw;
  blankMs: number;
  onReady: () => void;
}) {
  const [phase, setPhase] = useState<Phase>(
    blankMs > 0 ? "blank" : raw.typing ? "typing" : "reveal",
  );
  const [shown, setShown] = useState<string>(raw.streaming || raw.typewriter ? "" : text);
  const blankTimerRef = useRef<number | null>(null);
  const typingTimerRef = useRef<number | null>(null);
  const revealTimerRef = useRef<number | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    let alive = true;
    const finish = () => {
      if (alive) onReadyRef.current();
    };

    const reveal = () => {
      if (!alive) return;
      setPhase("reveal");
      if (raw.streaming) {
        // Reveal paragraph by paragraph (fall back to lines), like desktop.
        const byPara = text.split("\n\n");
        const parts = (byPara.length === 1 ? text.split("\n") : byPara).filter(
          (p) => p.trim() !== "",
        );
        if (parts.length === 0) {
          setShown(text);
          finish();
          return;
        }
        let acc = "";
        let i = 0;
        const step = () => {
          if (!alive) return;
          acc += (i > 0 ? "\n\n" : "") + parts[i];
          i += 1;
          setShown(acc);
          if (i < parts.length) {
            revealTimerRef.current = window.setTimeout(step, STREAM_CHUNK_MS);
          } else {
            finish();
          }
        };
        step();
      } else if (raw.typewriter) {
        void runTypewriter(
          text,
          raw.typewriterSpeedMs,
          (t) => {
            if (alive) setShown(t);
          },
          (id) => {
            revealTimerRef.current = id;
          },
        ).then(finish);
      } else {
        setShown(text);
        finish();
      }
    };

    const afterBlank = () => {
      if (!alive) return;
      if (raw.typing) {
        setPhase("typing");
        typingTimerRef.current = window.setTimeout(reveal, Math.max(0, raw.typingWaitMs));
      } else {
        reveal();
      }
    };

    if (blankMs > 0) {
      blankTimerRef.current = window.setTimeout(afterBlank, blankMs);
    } else {
      afterBlank();
    }

    return () => {
      alive = false;
      if (blankTimerRef.current !== null) clearTimeout(blankTimerRef.current);
      if (typingTimerRef.current !== null) clearTimeout(typingTimerRef.current);
      if (revealTimerRef.current !== null) clearTimeout(revealTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="jol-msg-row jol-msg-row--assistant">
      {phase === "typing" ? (
        <TypingIndicator label={raw.typingLabel} style={raw.typingStyle} fontSizePx={raw.fontPx} />
      ) : phase === "reveal" ? (
        <div className="jol-bubble jol-bubble--assistant" style={{ fontSize: `${raw.fontPx}px` }}>
          <AssistantMarkdown text={shown} />
        </div>
      ) : null}
    </div>
  );
}
